import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reviewsApi } from '@/api/reviews.api';
import { queryKeys } from '@/hooks/queryKeys';
import { StarRating } from '@/components/ui/StarRating';
import { Spinner } from '@/components/ui/Spinner';
import { Badge } from '@/components/ui/Badge';
import { formatDate } from '@/lib/formatters';
import { sanitizeReview } from '@/lib/sanitize';
import type { Review } from '@/types/review';
import { cn } from '@/lib/cn';

interface ProductReviewsProps {
  productId: number;
  averageRating: number;
  reviewCount: number;
  className?: string;
}

const PAGE_SIZE = 5;

function ratingBreakdown(reviews: Review[]) {
  const buckets = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 } as Record<1 | 2 | 3 | 4 | 5, number>;
  for (const r of reviews) {
    const k = Math.round(r.rating) as 1 | 2 | 3 | 4 | 5;
    if (k >= 1 && k <= 5) buckets[k] += 1;
  }
  return buckets;
}

export function ProductReviews({
  productId,
  averageRating,
  reviewCount,
  className,
}: ProductReviewsProps) {
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: [...queryKeys.reviews.forProduct(productId), page] as const,
    queryFn: () => reviewsApi.forProduct(productId, page, PAGE_SIZE),
    staleTime: 60_000,
  });

  const reviews = data?.items ?? [];
  const totalPages = data?.pagination.totalPages ?? 1;

  const buckets = useMemo(() => ratingBreakdown(reviews), [reviews]);
  const maxBucket = Math.max(1, ...Object.values(buckets));

  return (
    <section
      id="reviews"
      aria-labelledby="reviews-heading"
      className={cn('flex flex-col gap-8', className)}
    >
      <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2
            id="reviews-heading"
            className="font-[family-name:var(--font-editorial)] text-2xl text-white"
          >
            Customer Reviews
          </h2>
          <p className="text-sm text-[color:var(--color-text-secondary)]">
            {reviewCount} review{reviewCount === 1 ? '' : 's'}
          </p>
        </div>

        <div className="flex items-center gap-4">
          <span className="font-[family-name:var(--font-display)] text-5xl text-white">
            {averageRating.toFixed(1)}
          </span>
          <div className="flex flex-col gap-1">
            <StarRating value={averageRating} size={18} />
            <span className="text-xs text-[color:var(--color-text-muted)]">
              out of 5
            </span>
          </div>
        </div>
      </header>

      {/* Distribution */}
      <div className="grid grid-cols-1 gap-2 rounded-lg border border-white/[0.06] bg-white/[0.02] p-4">
        {([5, 4, 3, 2, 1] as const).map((star) => {
          const c = buckets[star];
          const pct = (c / maxBucket) * 100;
          return (
            <div key={star} className="flex items-center gap-3 text-sm">
              <span className="w-4 text-[color:var(--color-text-muted)]">{star}</span>
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/10">
                <div
                  className="h-full bg-[color:var(--color-gold)]"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="w-8 text-right text-[color:var(--color-text-muted)]">{c}</span>
            </div>
          );
        })}
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : reviews.length === 0 ? (
        <p className="rounded-lg border border-dashed border-white/10 p-8 text-center text-[color:var(--color-text-muted)]">
          No reviews yet. Be the first to share your experience.
        </p>
      ) : (
        <ul className="flex flex-col gap-6">
          {reviews.map((r) => (
            <li
              key={r.id}
              className="flex flex-col gap-3 border-b border-white/[0.06] pb-6 last:border-b-0"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="font-medium text-white">
                    {r.user?.name ?? 'Anonymous'}
                  </span>
                  <Badge variant="success">Verified Purchase</Badge>
                </div>
                <span className="text-xs text-[color:var(--color-text-muted)]">
                  {formatDate(r.createdAt)}
                </span>
              </div>
              <StarRating value={r.rating} size={14} />
              {r.title && (
                <h3 className="font-[family-name:var(--font-editorial)] text-lg text-white">
                  {r.title}
                </h3>
              )}
              {r.comment && (
                <p
                  className="text-[14px] leading-relaxed text-[color:var(--color-text-secondary)]"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeReview(r.comment),
                  }}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          {Array.from({ length: totalPages }).map((_, i) => {
            const p = i + 1;
            return (
              <button
                key={p}
                type="button"
                onClick={() => setPage(p)}
                aria-current={p === page}
                className={cn(
                  'inline-flex h-9 w-9 items-center justify-center rounded-md text-sm transition-colors',
                  p === page
                    ? 'bg-[color:var(--color-brand-magenta)] text-white'
                    : 'border border-white/10 text-[color:var(--color-text-secondary)] hover:bg-white/5'
                )}
              >
                {p}
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}
